<template>
  <div class="bg-light min-vh-100">
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-lg-8">
          <div class="card shadow-sm">
            <div class="card-body">
              <h1 class="h4 mb-2">Import Devices from CSV</h1>
              <p class="help mb-3">Upload a CSV file to bulk-create devices.</p>

              <!-- Expected columns reference -->
              <div class="mb-3">
                <div class="fw-semibold small mb-1">Expected CSV Columns:</div>
                <div class="small text-muted">
                  <code>serial_number</code> (required),
                  <code>device_type</code> (Laptop/Desktop/Other),
                  <code>template</code> (name or ID, required),
                  <code>device_make</code>,
                  <code>device_model</code>,
                  <code>device_commonname</code>,
                  <code>device_model_ref</code> (optional, name or ID),
                  <code>intake_condition</code> (optional)
                </div>
              </div>

              <!-- File picker -->
              <div class="mb-3">
                <label class="form-label">CSV File</label>
                <input type="file" class="form-control" accept=".csv" @change="onFileSelect" ref="fileInput" />
              </div>

              <!-- Preview of first 5 rows -->
              <div v-if="previewRows.length > 0" class="mb-3">
                <div class="fw-semibold small mb-1">Preview (first {{ previewRows.length }} rows):</div>
                <div class="table-responsive">
                  <table class="table table-sm table-bordered">
                    <thead class="table-light">
                      <tr>
                        <th v-for="col in previewColumns" :key="col" class="small">{{ col }}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(row, idx) in previewRows" :key="idx">
                        <td v-for="col in previewColumns" :key="col" class="small">{{ row[col] || "" }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Import button -->
              <button class="btn btn-primary" :disabled="!selectedFile || importing" @click="doImport">
                <span v-if="importing" class="spinner-border spinner-border-sm me-1"></span>
                Import
              </button>

              <!-- Error -->
              <div v-if="error" class="alert alert-danger py-2 mt-3">{{ error }}</div>

              <!-- Results -->
              <div v-if="results" class="mt-3">
                <div class="alert py-2" :class="results.error_count > 0 ? 'alert-warning' : 'alert-success'">
                  <strong>{{ results.success_count }}</strong> devices imported successfully.
                  <span v-if="results.error_count > 0">
                    <strong>{{ results.error_count }}</strong> rows had errors.
                  </span>
                </div>

                <div v-if="results.errors && results.errors.length > 0">
                  <div class="fw-semibold small mb-1">Errors:</div>
                  <ul class="small">
                    <li v-for="(e, idx) in results.errors" :key="idx">
                      Row {{ e.row }}: {{ e.error }}
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { APIService } from "../http/APIService";

export default {
  name: "CSVImport",
  data() {
    return {
      selectedFile: null,
      previewRows: [],
      previewColumns: [],
      importing: false,
      error: null,
      results: null,
      api: new APIService(),
    };
  },
  methods: {
    onFileSelect(event) {
      this.results = null;
      this.error = null;
      const file = event.target.files[0];
      if (!file) {
        this.selectedFile = null;
        this.previewRows = [];
        this.previewColumns = [];
        return;
      }
      this.selectedFile = file;
      this.parsePreview(file);
    },

    parsePreview(file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split("\n").filter((l) => l.trim());
        if (lines.length < 2) {
          this.previewColumns = [];
          this.previewRows = [];
          return;
        }
        const headers = lines[0].split(",").map((h) => h.trim());
        this.previewColumns = headers;
        const rows = [];
        for (let i = 1; i < Math.min(lines.length, 6); i++) {
          const values = lines[i].split(",");
          const row = {};
          headers.forEach((h, idx) => {
            row[h] = (values[idx] || "").trim();
          });
          rows.push(row);
        }
        this.previewRows = rows;
      };
      reader.readAsText(file);
    },

    async doImport() {
      if (!this.selectedFile) return;
      this.importing = true;
      this.error = null;
      this.results = null;

      try {
        const accessToken = localStorage.getItem("accessToken");
        const res = await this.api.importCSV(this.selectedFile, accessToken);
        this.results = res.data;
      } catch (err) {
        console.error("CSV import failed:", err);
        this.error = err.response?.data?.detail || "Import failed.";
      } finally {
        this.importing = false;
      }
    },
  },
};
</script>
